/* eslint-disable camelcase */
/**
 * Single source for READING a position's TP/SL for display.
 *
 * Three surfaces show the same two trigger prices — the desktop table cell
 * (components/Positions/TpslCell), the tablet card (PositionCardTpslValue) and
 * the mobile row (MobilePositionsTab/MobilePositionTpsl). Each of them had grown
 * its own copy of the same formatter, and they had already drifted: one rendered
 * "--" where the others rendered "-", and the card dropped the instrument's price
 * precision entirely, so the same trigger displayed with a different number of
 * decimals depending on viewport width.
 *
 * Writing TP/SL is the mirror of this: `useOrderFormTpsl` (new orders) and
 * `useTpslForPosition` (existing positions), both funnelling into the executor
 * client in this same directory.
 */
import { useMemo } from "react";
import currency from "currency.js";

import { GetAccount200ResponsePositionsInner } from "../../codegen-api";
import { useOrder } from "../api/order/useOrder";
import { TpslTriggersView } from "./tpsl.selectors";

/** What every TP/SL surface shows when a leg is not set. */
export const TPSL_EMPTY_LABEL = "-";

/**
 * A trigger price as text. "0" is the executor's "unset" value, not a real
 * price, so it renders as the empty label rather than "$0.00".
 */
export function formatTriggerPrice(
  value: string | undefined,
  precision: number,
  placeholder: string = TPSL_EMPTY_LABEL
): string {
  return value && Number(value) > 0
    ? currency(value, { precision }).format()
    : placeholder;
}

export interface PositionTpslDisplay {
  triggers: TpslTriggersView;
  takeProfit?: string;
  stopLoss?: string;
  /** True when at least one leg is live — drives "edit" vs "add" affordances. */
  hasTriggers: boolean;
  takeProfitLabel: string;
  stopLossLabel: string;
  /** False on chains with no trigger executor; surfaces hide themselves. */
  tpslSupported: boolean;
}

export function usePositionTpslDisplay(
  position: GetAccount200ResponsePositionsInner | undefined,
  pricePrecision: number,
  placeholder: string = TPSL_EMPTY_LABEL
): PositionTpslDisplay {
  // Triggers come from the ORDER CONTEXT, which merges the executor's open-trigger
  // list with the backend's `position.triggers` enrichment. The enrichment is
  // absent (not null) whenever the executor is momentarily unreachable, so reading
  // `position.triggers` directly renders a permanent "-".
  const { tpslTriggersForPosition, tpslSupported } = useOrder();

  return useMemo(() => {
    const triggers = tpslTriggersForPosition(position);
    const takeProfit = triggers.take_profit?.trigger;
    const stopLoss = triggers.stop_loss?.trigger;

    return {
      triggers,
      takeProfit,
      stopLoss,
      hasTriggers: Boolean(takeProfit || stopLoss),
      takeProfitLabel: formatTriggerPrice(takeProfit, pricePrecision, placeholder),
      stopLossLabel: formatTriggerPrice(stopLoss, pricePrecision, placeholder),
      tpslSupported,
    };
  }, [
    position,
    placeholder,
    pricePrecision,
    tpslSupported,
    tpslTriggersForPosition,
  ]);
}
